import { useTranslation } from "react-i18next"
import { Markdown } from "../modules/markdown/Markdown"
import { DARK_THEME } from "../common/theming/darkTheme"
import { LIGHT_THEME } from "../common/theming/lightTheme"
import { EmbedField } from "../modules/message/preview/EmbedField"
import { isValid } from "date-fns"
import { RichEmbedContainer } from "../modules/message/preview/RichEmbedContainer"
import { Observer } from "mobx-react-lite"
import { rem } from "polished"
import { useRef, useContext } from "react"
import styled, { css, ThemeProvider } from "styled-components"
import { useReaction } from "../common/state/useReaction"
import { MarkdownContainer } from "../modules/markdown/styles/MarkdownContainer"
import { EmbedAuthor } from "../modules/message/preview/EmbedAuthor"
import { EmbedFooter } from "../modules/message/preview/EmbedFooter"
import { EmbedGallery } from "../modules/message/preview/EmbedGallery"
import { ThemeContext } from "../contexts/theme"
const EmbedGrid = styled.div`
    padding: ${rem(8)} ${rem(16)} ${rem(16)} ${rem(12)};
    display: inline-grid;

    grid-template-columns: auto;
    grid-template-rows: auto;
`
const EmbedTitleNormal = styled.span`
    min-width: 0;

    display: inline-block;
    margin: 8px 0 0;
    grid-column: 1 / 2;

    & > ${MarkdownContainer} {
        font-size: ${rem(16)};
        font-weight: 600;
        color: ${({ theme }) => theme.header.primary};
    }
`

const EmbedTitleLink = styled.span`
    min-width: 0;

    display: inline-block;
    margin: 8px 0 0;
    grid-column: 1 / 2;

    & > ${MarkdownContainer} {
    color: ${({ theme }) => theme.text.link};
    }
`

const EmbedDescription = styled.div`
    min-width: 0;

    margin: 8px 0 0;
    grid-column: 1 / 2;

    & > ${MarkdownContainer} {
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.text.normal};

    line-height: ${rem(18)};
    white-space: pre-line;
    }
`

const EmbedFields = styled.div`
    min-width: 0;

    margin: 8px 0 0;

    display: grid;
    grid-column: 1 / 2;
    grid-gap: 8px;
`

const EmbedImage = styled.img.attrs((props) => ({
    $hasthumbnail: props.$hasthumbnail,
}))`
    min-width: 0;
    width: 100%;
    max-width: 400px;
    max-height: 300px;

    margin: 16px 0 0;
    border-radius: 4px;

    grid-column: 1 / 2;

    ${({ $hasthumbnail }) =>
            $hasthumbnail === "true" &&
            css`
    grid-column: 1 / 3;
    `};
`

const EmbedThumbnailContainer = styled.div`
    margin: 8px 0 0 16px;
    grid-row: 1 / 8;
    grid-column: 2 / 3;
    justify-self: end;
`

const EmbedThumbnail = styled.img`
    max-width: 80px;
    max-height: 80px;

    border-radius: 4px;
`
export function ProfileEmbed({ embed, roles }) {
    const { t } = useTranslation()
    function colorToHexString(number) {
        let string = number.toString(16)
        const length = string.length
        for (let i = 0; i < 6 - length; i++) {
            string = "0" + string
        }
        return "#" + string
    }
    function hexStringToColor(hex, alpha) {
        const r = parseInt(hex.slice(1, 3), 16),
            g = parseInt(hex.slice(3, 5), 16),
            b = parseInt(hex.slice(5, 7), 16),
            a = alpha;
        return `rgba(${r}, ${g}, ${b}, ${a})`
    }
    const containerRef = useRef(null)
    const imageRef = useRef(null)

    useReaction(
        () => embed.gallery.length === 1 && embed.gallery[0],
        () => {
            const { current: container } = containerRef
            if (!container) return

            container.style.maxWidth = "432px"
        },
    )
    const [ Theme ] = useContext(ThemeContext)
    return (<Observer>{() => {
        const color = embed.color
        return (
            <ThemeProvider theme={Theme === "dark" ? DARK_THEME : LIGHT_THEME}>
                <RichEmbedContainer ref={containerRef} style={{ borderColor: color, maxWidth: "432px" }}>   
                    <EmbedGrid>
                        {embed.author?.name.trim().length > 0 && <EmbedAuthor embed={embed} />}
                        {embed.title.trim().length > 0 &&
                            (embed.url ? (
                                <EmbedTitleLink
                                    as="a"
                                    href={embed.url}
                                    rel="noopener noreferrer nofollow ugc"
                                    target="_blank"
                                >
                                    <Markdown content={embed.title} type="embed-header" />
                                </EmbedTitleLink>
                            ) : (
                                <EmbedTitleNormal>
                                    <Markdown content={embed.title} type="embed-header" />
                                </EmbedTitleNormal>
                            ))}
                        {embed.description?.trim()?.length > 0 && (
                            <EmbedDescription>
                                <Markdown content={embed.description} type="embed-content" />
                            </EmbedDescription>
                        )}
                        {embed.fields.length > 0 && (
                            <EmbedFields>
                                {embed.fields.map(field => {
                                    if (field.type !== "roles") return (<EmbedField key={field.id} field={field} embed={embed} />)
                                    else {
                                        return (
                                            <div key={field.id} className="embedField" style={{ gridColumn: "1 / 13", color: "white" }}>
                                                <div key={field.id} className="embedFieldName">
                                                    <span style={{ color: "var(--color)" }}>{field.name}</span>
                                                </div>
                                                <div className="embedFieldValue" style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                                                    {roles.slice(0, 10).map(role => {
                                                        return (<span key={role.id} className="roleMention wrapper" style={{ color: role.color !== 0 ? hexStringToColor(colorToHexString(role.color), 1) : Theme === "dark" ? "hsl( 235 calc( 1 * 86.2%) 88.6% / 1)" : "hsl(235 calc(1 * 85.6%) 64.7% / 1)", backgroundColor: role.color !== 0 ? hexStringToColor(colorToHexString(role.color), 0.1) : "hsl( 235 calc( 1 * 85.6%) 64.7% / 0.3)", display: "flex", width: "fit-content" }}>
                                                            <span style={{ color: "unset" }}>{`@${role.name}`}</span>
                                                        </span>)
                                                    })}
                                                    {roles.length > 10 ? <em><span style={{ color: "var(--color)" }}> {t("и ещё")} {roles.length - 10}...</span></em> : undefined}
                                                </div>
                                            </div>
                                        )
                                    }
                                })}
                            </EmbedFields>
                        )}
                        {embed.gallery.length > 1 ? (
                            <EmbedGallery embed={embed} />
                        ) : embed.gallery.length === 1 ? (
                            <EmbedImage
                                ref={imageRef}
                                src={embed.gallery[0]}
                                alt="Image"
                                $hasthumbnail={`${Boolean(embed.thumbnail)}`}
                                // onLoad={() => {
                                //     const { current: container } = containerRef
                                //     const { current: image } = imageRef
                                //     if (!container || !image) return

                                //     const { width } = image.getBoundingClientRect()
                                //     container.style.maxWidth = width >= 300 ? `${width + 32}px` : ""
                                // }}
                            />
                        ) : undefined}
                        {(embed.footer?.text?.trim()?.length > 0 || isValid(embed.timestamp)) && <EmbedFooter embed={embed} />}
                        {embed.thumbnail && (
                            <EmbedThumbnailContainer>
                                <EmbedThumbnail src={embed.thumbnail} alt="Thumbnail" />
                            </EmbedThumbnailContainer>
                        )}
                    </EmbedGrid>
                </RichEmbedContainer>
            </ThemeProvider>
        )
    }}</Observer>)
}